import { useEffect, useState, useTransition, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { DateInput } from "./DateInput";
import { ORDER_STATUS, formatActivityDate } from "./orderLabels";
import { useChangeLines } from "./OrderLog";
import { ordersKeys, type OrderStatus, type RepeatResult } from "./orders.api";
import {
  compareListRows,
  orderListTemplateKeys,
  type MailLists,
  type OrderListTemplate,
  type RowComparison,
} from "./orderListTemplates.api";
import { intakeKeys, intakeListOrders } from "@/features/intake/intake.api";
import {
  addDays,
  bestList,
  guessFields,
  metresText,
  mondayOf,
  parseNumber,
  prefixFor,
  rowsOfList,
  usesWeekdays,
  type FieldMap,
  type ListField,
  type ListRow,
  type ListRowInput,
  type ListSettings,
  type MailList,
} from "./listRows";

// Orders inlezen from the invoerbak (docs/tms-orderintake.md §3g, docs/invoerbak.md §3b): the orders of a customer's
// list. WLX reads the lists in the mail the bak kept — an Excel or CSV attachment, a table in the mail — and the
// ordermail of the sender says which customer, what every order has in common, which column fills which field. The
// person checks the rows, unticks what is not ours, and makes the orders; what WLX does not know (an address) waits in
// Aandacht nodig, as from EDI. The columns are remembered on the ordermail, so the next mail of that customer reads the
// same way. The ordermail itself is made and changed by an admin on the relation card (OrdermailForm.tsx).

const FIELD_GROUPS: { titleKey: string; fields: ListField[]; extra?: ListField[] }[] = [
  { titleKey: "orders.mailGroupOrder", fields: ["reference", "name", "pallets", "carrier", "loadingMeters", "weightKg", "description", "remark"] },
  { titleKey: "orders.mailGroupLoad", fields: ["loadDate", "loadTime", "loadReference", "loadInstruction"], extra: ["loadName", "loadStreet", "loadPostalCode", "loadCity", "loadCountry"] },
  { titleKey: "orders.mailGroupUnload", fields: ["unloadDate", "unloadTime", "unloadReference", "unloadInstruction", "unloadName", "unloadStreet", "unloadPostalCode", "unloadCity", "unloadCountry"] },
];

/** The Monday of the week after the mail: a weekly planning mailed on Friday is for the week after. */
export function weekAfter(receivedAt: string | null): string {
  const day = (receivedAt ?? new Date().toISOString()).slice(0, 10);
  return addDays(mondayOf(day), 7);
}

/** "euro=0,4; blok=0,5" as a map, and back. */
export function carriersOf(text: string): Record<string, number> {
  const map: Record<string, number> = {};
  for (const part of text.split(/[;\n]/)) {
    const [key, value] = part.split("=");
    const metres = value ? parseNumber(value) : null;
    if (key?.trim() && metres && metres > 0) map[key.trim().toLowerCase()] = metres;
  }
  return map;
}
export const carriersText = (map: Record<string, number>) =>
  Object.entries(map)
    .map(([key, metres]) => `${key}=${metresText(metres)}`)
    .join("; ");

/** The value once it stood still for a moment: the rows are compared when the person stops typing, not on every key. */
function useSettled(value: string, ms: number): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

/** When a row loads or unloads, as the table says it: "di 3 nov 01:00". */
export function whenOfSide(side: { date: string; from?: string; to?: string }): string {
  return side.date ? `${formatActivityDate(side.date)}${side.from ? ` ${side.from}${side.to && side.to !== side.from ? `–${side.to}` : ""}` : ""}` : "—";
}

/** A row as its reference: what a comparison and a yes to *Bijwerken* hang on, whichever rows are sent. */
const referenceKey = (row: ListRow) => row.input.customerReference.trim().toLowerCase();

/** The lists of a row of the bak, read with an ordermail; no ordermail yet: one made here. */
export function OrderListsPanel({ itemId, mail, templates }: { itemId: string; mail: MailLists; templates: OrderListTemplate[] }) {
  const { t } = useTranslation();
  const [channelId, setChannelId] = useState<string | null>(mail.channelId);
  // Not chosen: the table whose header holds the ordermail's columns, which a forward need not put first.
  const [listKey, setListKey] = useState<string | null>(null);
  const channel = templates.find((template) => template.id === channelId);
  const list = mail.lists.find((one) => one.key === listKey) ?? bestList(mail.lists, channel?.fields ?? {});

  if (mail.lists.length === 0) {
    return (
      <div className="wl-alert wl-alert--info">
        <span>{mail.unread.length > 0 ? t("orders.mailUnreadable", { files: mail.unread.join(", ") }) : t("orders.mailNoList")}</span>
      </div>
    );
  }

  return (
    <>
      <span className="t-body-sm t-muted">
        {t("orders.mailFrom", { from: mail.mail.fromName ?? mail.mail.from ?? "—" })}
        {mail.mail.forwardedFrom && ` · ${t("orders.mailForwardedFrom", { from: mail.mail.forwardedFrom })}`}
      </span>
      <div className="wl-hstack" style={{ gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className="wl-field" style={{ minWidth: "280px" }}>
          <label className="wl-field__label" htmlFor="mail-channel">
            {t("orders.mailChannel")}
          </label>
          <select id="mail-channel" className="wl-select" value={channelId ?? ""} onChange={(e) => setChannelId(e.target.value || null)}>
            <option value="">{t("orders.mailChannelNone")}</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.name} · {template.party.name}
              </option>
            ))}
          </select>
        </div>
        {mail.lists.length > 1 && (
          <div className="wl-field" style={{ minWidth: "220px" }}>
            <label className="wl-field__label" htmlFor="mail-list">
              {t("orders.mailList")}
            </label>
            <select id="mail-list" className="wl-select" value={list?.key ?? ""} onChange={(e) => setListKey(e.target.value)}>
              {mail.lists.map((one) => (
                <option key={one.key} value={one.key}>
                  {one.label || t("orders.mailBodyTable")} ({t("orders.mailRowCount", { count: one.rows.length })})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      {!channelId && <span className="t-caption t-muted">{t("orders.mailNoChannel")}</span>}

      {channel && list && <ListReader key={`${channel.id}|${list.key}|${channel.changedAt ?? ""}`} channel={channel} list={list} mail={mail} itemId={itemId} />}
    </>
  );
}

function ListReader({ channel, list, mail, itemId }: { channel: OrderListTemplate; list: MailList; mail: MailLists; itemId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // The columns are the ordermail's, set by an admin on the relation card (Camiel, 25-10-2026: "dit moet vanuit de relatie
  // ... zodat de orderbak puur blijft om een order aan te maken"); none set yet, the plain ones guessed.
  const remembered = Object.keys(channel.fields).length > 0;
  const fields: FieldMap = remembered ? channel.fields : guessFields(list.header);
  const [loadDate, setLoadDate] = useState(mail.dates.load ?? "");
  const [unloadDate, setUnloadDate] = useState(mail.dates.unload ?? "");
  // The week the subject names ("planning week 45"), else the week after the mail.
  const [week, setWeek] = useState(() => mail.dates.week ?? weekAfter(mail.mail.receivedAt));
  const [prefixTyped, setPrefixTyped] = useState<string | null>(null);
  const [perPallet, setPerPallet] = useState(metresText(channel.metresPerPallet));
  const [provisional, setProvisional] = useState(channel.provisional);
  const [noInvoice, setNoInvoice] = useState(channel.noInvoiceReason !== null);
  const [noInvoiceReason, setNoInvoiceReason] = useState(channel.noInvoiceReason ?? t("orders.repeatNoInvoiceDefault"));
  const [unticked, setUnticked] = useState<Set<number>>(() => new Set());
  // The rows whose changes to an order that exists the person agreed to, by reference (Bijwerken).
  const [agreed, setAgreed] = useState<Set<string>>(() => new Set());
  const [result, setResult] = useState<RepeatResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();
  const changeLines = useChangeLines();

  const weekdays = usesWeekdays(list, fields);
  const prefix = prefixTyped ?? prefixFor(channel.referencePrefix, weekdays ? week : loadDate || unloadDate || week);
  const settings: ListSettings = {
    fields,
    loadDate: loadDate || null,
    unloadDate: unloadDate || null,
    week,
    prefix,
    metresPerPallet: parseNumber(perPallet),
    metresByCarrier: channel.metresByCarrier,
    loadDateRule: channel.loadDateRule,
    rowFilter: channel.rowFilter,
    placeRules: channel.placeRules,
  };
  const rows = rowsOfList(list, settings);
  const madeIndexes = new Set(result?.made.map((made) => made.index) ?? []);
  const updatedIndexes = new Set(result?.updated?.map((updated) => updated.index) ?? []);
  const done = (row: ListRow) => madeIndexes.has(row.index) || updatedIndexes.has(row.index);

  // Every row that could go is held against the customer's orders first (Camiel, 25-10-2026: "misschien moet je de
  // verschillen wel eerst in kaart brengen zodat we akkoord geven"): new, or what it would change on the order that has
  // its reference. Nothing goes before WLX knows which.
  const comparable = rows.filter((row) => !row.filtered && row.problems.length === 0 && !done(row));
  const compareKey = JSON.stringify(comparable.map((row) => row.input));
  const settledKey = useSettled(compareKey, 400);
  const comparison = useQuery({
    queryKey: orderListTemplateKeys.compare(channel.id, settledKey),
    queryFn: () => compareListRows(channel.id, JSON.parse(settledKey) as ListRowInput[]),
    enabled: comparable.length > 0 && settledKey === compareKey,
  });
  const fresh = settledKey === compareKey && comparison.data !== undefined;
  const compared = new Map<string, RowComparison>();
  if (fresh) comparable.forEach((row, i) => comparison.data![i] && compared.set(referenceKey(row), comparison.data![i]!));
  const comparing = comparable.length > 0 && !fresh && !comparison.isError;
  const comparisonOf = (row: ListRow) => compared.get(referenceKey(row));
  const changedRows = comparable.filter((row) => comparisonOf(row)?.kind === "changed");

  const takes = (row: ListRow) => {
    if (row.filtered || row.problems.length > 0 || done(row) || unticked.has(row.index)) return false;
    const one = comparisonOf(row);
    return one?.kind === "new" || (one?.kind === "changed" && agreed.has(referenceKey(row)));
  };
  const chosen = rows.filter(takes);
  const updates = chosen.filter((row) => comparisonOf(row)?.kind === "changed").length;
  const makes = chosen.length - updates;

  function submit() {
    setError(null);
    startTransition(async () => {
      try {
        const answer = await intakeListOrders(itemId, {
          templateId: channel.id,
          rows: chosen.map((row) => row.input),
          update: chosen.flatMap((row, i) => (comparisonOf(row)?.kind === "changed" ? [i] : [])),
          provisional,
          noInvoiceReason: noInvoice ? noInvoiceReason.trim() : null,
        });
        // The answer counts by the rows sent: back to the rows of the list.
        const sent = chosen.map((row) => row.index);
        setResult((before) => ({
          made: [...(before?.made ?? []), ...answer.made.map((made) => ({ ...made, index: sent[made.index]! }))],
          updated: [...(before?.updated ?? []), ...(answer.updated ?? []).map((updated) => ({ ...updated, index: sent[updated.index]! }))],
          failed: answer.failed.map((failed) => ({ ...failed, index: sent[failed.index]! })),
        }));
        void queryClient.invalidateQueries({ queryKey: ordersKeys.all });
        void queryClient.invalidateQueries({ queryKey: orderListTemplateKeys.all });
        void queryClient.invalidateQueries({ queryKey: intakeKeys.all });
      } catch (err) {
        setError(err instanceof Error ? err.message : t("common.errorGeneric"));
      }
    });
  }

  const failedOf = (index: number) => result?.failed.find((failed) => failed.index === index);
  const madeOf = (index: number) => result?.made.find((made) => made.index === index);
  const updatedOf = (index: number) => result?.updated?.find((updated) => updated.index === index);
  const waiting = result?.made.filter((made) => made.status === "blocked").length ?? 0;
  const updatedCount = result?.updated?.length ?? 0;
  const held = result?.updated?.filter((updated) => updated.outcome === "held").length ?? 0;
  const failedReason = (code: string) =>
    code === "DUPLICATE_REFERENCE"
      ? t("orders.repeatFailedDuplicate")
      : code === "NOTHING_TO_UPDATE"
        ? t("orders.repeatFailedNothingToUpdate")
        : code === "VALIDATION_ERROR"
          ? t("orders.repeatFailedInvalid")
          : t("orders.repeatFailedOther");
  const statusText = (status: string) => {
    const key = ORDER_STATUS[status as OrderStatus]?.labelKey;
    return key ? t(key as never) : status;
  };
  // What WLX found for a row: new, or the order that has its reference and what the row would change on it.
  const compareNote = (one: RowComparison): ReactNode => {
    if (one.kind === "new") return t("orders.mailRowNew");
    if (one.kind === "invalid") return t("orders.mailRowInvalid", { message: one.error?.message ?? "" });
    const order = one.order!;
    const linked = (text: string) => (
      <Link to="/orders/$id" params={{ id: order.id }}>
        {text}
      </Link>
    );
    if (one.kind === "closed") return linked(t("orders.mailRowClosed", { number: order.number, status: statusText(order.status) }));
    if (one.kind === "unchanged") return linked(t("orders.mailRowSame", { number: order.number }));
    const trips = order.trips.map((trip) => trip.tripNumber).join(", ");
    return (
      <div className="wl-vstack" style={{ gap: "2px", alignItems: "flex-start" }}>
        {linked(t("orders.mailRowExists", { number: order.number }))}
        {changeLines(one.changes).map((line) => (
          <span key={line}>{line}</span>
        ))}
        {trips && <span style={{ color: one.held ? "var(--text-danger)" : undefined }}>{t(one.held ? "orders.mailRowHeld" : "orders.mailRowOnTrip", { trips })}</span>}
      </div>
    );
  };
  const toggle = (set: typeof setAgreed, key: string) =>
    set((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const where = (row: ListRow) => {
    const place = row.ruled.unload ?? row.input.unload.place;
    return place ? [place.name, place.city].filter(Boolean).join(", ") : channel.unload ? `${channel.unload.name}, ${channel.unload.city}` : "—";
  };
  const whenText = (side: { date: string; from?: string; to?: string }) =>
    side.date ? `${formatActivityDate(side.date)}${side.from ? ` ${side.from}${side.to ? `–${side.to}` : ""}` : ""}` : "—";

  return (
    <div className="wl-vstack" style={{ gap: "14px", alignItems: "stretch" }}>
      <div className="wl-card" style={{ padding: "var(--space-3) var(--space-4)" }}>
        <div className="wl-vstack" style={{ gap: "2px", alignItems: "flex-start" }}>
          <span className="t-body-sm" style={{ fontWeight: "var(--fw-semibold)" }}>
            {channel.party.name} · {channel.load ? `${channel.load.name}, ${channel.load.city}` : "—"} → {channel.unload ? `${channel.unload.name}, ${channel.unload.city}` : "—"}
          </span>
          <span className="t-caption t-muted">
            {[channel.goods.description].filter(Boolean).join(" · ")}
            {remembered ? ` · ${t("orders.mailColumnsRemembered")}` : ` · ${t("orders.mailColumnsGuessed")}`}
          </span>
        </div>
      </div>

      {result && (
        <div className={`wl-alert ${result.failed.length > 0 ? "wl-alert--warning" : "wl-alert--success"}`} role="status">
          <div className="wl-vstack" style={{ gap: "4px", alignItems: "stretch" }}>
            <span>
              {(result.made.length > 0 || updatedCount === 0) && t("orders.repeatMade", { count: result.made.length })}
              {waiting > 0 && ` ${t("orders.mailWaiting", { count: waiting })}`}
              {updatedCount > 0 && ` ${t("orders.mailUpdated", { count: updatedCount })}`}
              {held > 0 && ` ${t("orders.mailUpdatedHeld", { count: held })}`}
            </span>
            {result.made.length + updatedCount > 0 && (
              <span className="t-caption">
                {[...result.made, ...(result.updated ?? [])].map((one, i) => (
                  <span key={one.id}>
                    {i > 0 && ", "}
                    <Link to="/orders/$id" params={{ id: one.id }} className="t-mono">
                      {one.number}
                    </Link>
                  </span>
                ))}
              </span>
            )}
            {result.failed.length > 0 && <span>{t("orders.repeatFailed", { count: result.failed.length })}</span>}
          </div>
        </div>
      )}

      <div className="wl-hstack" style={{ gap: "12px", alignItems: "flex-end", flexWrap: "wrap" }}>
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="mail-load-date">
            {t("orders.mailLoadDate")}
          </label>
          <DateInput id="mail-load-date" value={loadDate} onChange={setLoadDate} />
        </div>
        <div className="wl-field">
          <label className="wl-field__label" htmlFor="mail-unload-date">
            {t("orders.mailUnloadDate")}
          </label>
          <DateInput id="mail-unload-date" value={unloadDate} onChange={setUnloadDate} />
        </div>
        {weekdays && (
          <div className="wl-field">
            <label className="wl-field__label" htmlFor="mail-week">
              {t("orders.repeatWeek")}
            </label>
            <DateInput id="mail-week" value={week} onChange={(value) => value && setWeek(value)} />
          </div>
        )}
        <div className="wl-field" style={{ minWidth: "200px" }}>
          <label className="wl-field__label" htmlFor="mail-prefix">
            {t("orders.repeatPrefix")}
          </label>
          <input id="mail-prefix" className="wl-input" value={prefix} maxLength={40} onChange={(e) => setPrefixTyped(e.target.value)} />
        </div>
        <div className="wl-field" style={{ width: "140px" }}>
          <label className="wl-field__label" htmlFor="mail-per-pallet">
            {t("orders.repeatPerPallet")}
          </label>
          <input id="mail-per-pallet" className="wl-input t-mono" inputMode="decimal" value={perPallet} onChange={(e) => setPerPallet(e.target.value)} />
        </div>
      </div>
      <span className="t-caption t-muted">{t("orders.mailDatesHelp")}</span>

      <div className="wl-vstack" style={{ gap: "4px", alignItems: "flex-start" }}>
        <span className="t-caption t-muted">
          {FIELD_GROUPS.flatMap((group) => [...group.fields, ...(group.extra ?? [])])
            .filter((field) => fields[field])
            .map((field) => `${t(`orders.mailField.${field}` as never)}: ${fields[field]}`)
            .join(" · ") || t("orders.mailColumnsNone")}
        </span>
        <span className="t-caption t-muted">{t("orders.mailColumnsOnCard")}</span>
      </div>

      {comparing && <span className="t-caption t-muted">{t("orders.mailCompareBusy")}</span>}
      {comparison.isError && (
        <div className="wl-alert wl-alert--danger" role="alert">
          <span>{t("orders.mailCompareFailed", { message: comparison.error instanceof Error ? comparison.error.message : "" })}</span>
        </div>
      )}
      {changedRows.length > 0 && (
        <div className="wl-alert wl-alert--warning" role="status">
          <div className="wl-hstack" style={{ gap: "10px", alignItems: "center", flexWrap: "wrap" }}>
            <span>{t("orders.mailChangedCount", { count: changedRows.length })}</span>
            <button type="button" className="wl-btn wl-btn--secondary wl-btn--sm" onClick={() => setAgreed(new Set(changedRows.map(referenceKey)))}>
              {t("orders.mailUpdateAll")}
            </button>
            <button type="button" className="wl-btn wl-btn--ghost wl-btn--sm" onClick={() => setAgreed(new Set())}>
              {t("orders.mailUpdateNone")}
            </button>
          </div>
        </div>
      )}

      <div className="wl-table-wrap" style={{ maxHeight: "none" }}>
        <table className="wl-table wl-table--compact">
          <thead>
            <tr>
              <th aria-label={t("orders.mailTake")} />
              <th>{t("orders.repeatColReference")}</th>
              <th>{t("orders.repeatColLoad")}</th>
              <th>{t("orders.repeatColUnload")}</th>
              <th style={{ textAlign: "right" }}>{t("orders.repeatColPallets")}</th>
              <th style={{ textAlign: "right" }}>{t("orders.repeatColMetres")}</th>
              <th style={{ textAlign: "right" }}>{t("orders.mailColWeight")}</th>
              <th>{t("orders.mailColNote")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const made = madeOf(row.index);
              const updated = updatedOf(row.index);
              const failed = failedOf(row.index);
              const one = comparisonOf(row);
              const open = !row.filtered && row.problems.length === 0 && !done(row);
              // A row that changes an order that exists is ticked only with a yes: the box is *Bijwerken*.
              const isChange = one?.kind === "changed";
              const goes = one?.kind === "new" || isChange;
              const note: ReactNode = made
                ? t(made.status === "blocked" ? "orders.mailRowWaiting" : "orders.mailRowMade", { number: made.number })
                : updated
                  ? t(updated.outcome === "held" ? "orders.mailRowUpdatedHeld" : updated.outcome === "unchanged" ? "orders.mailRowUpdatedSame" : "orders.mailRowUpdated", {
                      number: updated.number,
                    })
                  : failed
                    ? failedReason(failed.code)
                    : row.filtered
                      ? t("orders.mailRowFiltered")
                      : row.problems.length > 0
                        ? row.problems.map((problem) => t(`orders.mailProblem.${problem}` as never)).join(" · ")
                        : one
                          ? compareNote(one)
                          : "";
              const warn = Boolean(failed) || (open === false && !row.filtered && !done(row)) || one?.kind === "invalid";
              return (
                <tr key={row.index} style={(!open && !done(row)) || (open && one && !goes) ? { color: "var(--text-tertiary)" } : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={isChange ? t("orders.mailUpdateAgree") : t("orders.mailTake")}
                      title={isChange ? t("orders.mailUpdateAgree") : undefined}
                      checked={takes(row) || done(row)}
                      disabled={!open || !goes}
                      onChange={() =>
                        isChange
                          ? toggle(setAgreed, referenceKey(row))
                          : setUnticked((current) => {
                              const next = new Set(current);
                              if (next.has(row.index)) next.delete(row.index);
                              else next.add(row.index);
                              return next;
                            })
                      }
                    />
                  </td>
                  <td className="t-mono">{row.input.customerReference || "—"}</td>
                  <td>{whenText(row.input.load)}</td>
                  <td>
                    <div className="wl-table__cell-stack">
                      <span className="primary">{where(row)}</span>
                      <span className="secondary">{whenText(row.input.unload)}</span>
                    </div>
                  </td>
                  <td style={{ textAlign: "right" }}>{row.input.pallets ?? "—"}</td>
                  <td style={{ textAlign: "right" }}>{metresText(row.input.loadingMeters) || "—"}</td>
                  <td style={{ textAlign: "right" }}>{row.input.weightKg ?? "—"}</td>
                  <td className="t-caption" style={warn ? { color: "var(--text-danger)" } : undefined}>
                    {note}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="wl-vstack" style={{ gap: "8px", alignItems: "stretch" }}>
        <label className="wl-hstack" style={{ gap: "8px", alignItems: "flex-start" }}>
          <input type="checkbox" checked={provisional} onChange={(e) => setProvisional(e.target.checked)} />
          <span className="wl-vstack" style={{ gap: "2px", alignItems: "flex-start" }}>
            <span className="t-body-sm">{t("orders.repeatProvisional")}</span>
            <span className="t-caption t-muted">{t("orders.repeatProvisionalHelp")}</span>
          </span>
        </label>
        <label className="wl-hstack" style={{ gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <input type="checkbox" checked={noInvoice} onChange={(e) => setNoInvoice(e.target.checked)} />
          <span className="t-body-sm">{t("orders.repeatNoInvoice")}</span>
          {noInvoice && (
            <input
              className="wl-input"
              aria-label={t("orders.repeatNoInvoiceReason")}
              value={noInvoiceReason}
              maxLength={200}
              style={{ width: "260px" }}
              onChange={(e) => setNoInvoiceReason(e.target.value)}
            />
          )}
        </label>
      </div>

      {error && (
        <div className="wl-alert wl-alert--danger" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className="wl-hstack" style={{ gap: "10px", alignItems: "center" }}>
        <button
          type="button"
          className="wl-btn wl-btn--accent"
          disabled={isPending || comparing || chosen.length === 0 || (noInvoice && !noInvoiceReason.trim())}
          onClick={submit}
        >
          {updates === 0
            ? t("orders.repeatSubmit", { count: makes })
            : makes === 0
              ? t("orders.mailSubmitUpdate", { count: updates })
              : t("orders.mailSubmitBoth", { made: makes, updated: updates })}
        </button>
        <span className="t-caption t-muted">{t("orders.mailSubmitHelp")}</span>
      </div>
    </div>
  );
}
